import { supabase } from './supabase';
import type { TimeRecord } from '../types/timeRecord';
import type { Database } from './database.types';

type TimeRecordRow = Database['public']['Tables']['time_records']['Row'];

export const fetchTimeRecord = async (date: string, userId: string) => {
  try {
    const { data, error } = await supabase
      .from('time_records')  // Changed to time_records to match upsert
      .select()
      .eq('record_date', date)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to fetch time record:', error);
    throw error;
  }
};

interface TimeRecordInput {
  userId: string;
  recordDate: string;
  amIn: string;
  amOut: string;
  pmIn: string;
  pmOut: string;
  otIn: string;
  otOut: string;
  salesAmount?: number;
  caAmount?: number;
  comments: string;
}

export const upsertTimeRecord = async (input: TimeRecordInput) => {
  try {
    const { error } = await supabase
      .from('time_records')
      .upsert({
        user_id: input.userId,
        record_date: input.recordDate,
        am_in: input.amIn || null,
        am_out: input.amOut || null,
        pm_in: input.pmIn || null,
        pm_out: input.pmOut || null,
        ot_in: input.otIn || null,
        ot_out: input.otOut || null,
        sales_amount: input.salesAmount || 0,
        ca_amount: input.caAmount || 0,
        comments: input.comments || '',
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    return { error };
  } catch (error) {
    console.error('Error upserting time record:', error);
    return { error };
  }
};

export const deleteTimeRecord = async (date: string, userId: string) => {
  try {
    const { error } = await supabase
      .from('time_records')
      .delete()
      .match({ record_date: date, user_id: userId });

    if (error) throw error;
    return { data: null, error: null };
  } catch (error) {
    console.error('Failed to delete time record:', error);
    throw error;
  }
};

export const fetchRecentTimeRecords = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('thirsty_records')  // Use thirsty_records view
      .select()
      .eq('user_id', user.id)
      .order('record_date', { ascending: false })
      .limit(30);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Failed to fetch recent records:', error);
    throw error;
  }
};

export const fetchAllTimeRecords = async (startDate?: string, endDate?: string, userId?: string) => {
  try {
    const query = supabase
      .from('thirsty_records')  // Use thirsty_records view
      .select()
      .order('record_date', { ascending: false });

    if (startDate) {
      query.gte('record_date', startDate);
    }
    if (endDate) {
      query.lte('record_date', endDate);
    }
    if (userId) {
      query.eq('user_id', userId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Failed to fetch all records:', error);
    throw error;
  }
};